import React, { ComponentProps, FC, useMemo } from "react";
import { OrderButtons } from "../order-buttons";
import { ItemsConfig } from "@services/tracpage/types/trac-page-items-config";
import { SectionIdentifier } from "@services/tracpage/types/trac-page-section-id";
import { useEditMode } from "../../../contexts/edit";
import { EMPTY_ITEMS_CONFIG } from "../../../constants/empty-items-config";
import {
  deleteItem,
  insertItem,
  updateMainOrder,
  updateOrder,
} from "../../../utils/items-config";
import { getSortMusicFn } from "../../../utils/items-sort";

type Props = {
  disabledState?: ComponentProps<typeof OrderButtons>["disabledState"];
  sectionId: SectionIdentifier;
  itemId: string;
  forMainPageOrder?: boolean;
};

export const ItemsOrderButtons: FC<Props> = ({
  disabledState,
  sectionId,
  itemId,
  forMainPageOrder = false,
}) => {
  const {
    draft,
    merch,
    events,
    editHandlers: { onItemsReorder },
  } = useEditMode();

  const relevantItems = useMemo(() => {
    switch (sectionId) {
      case SectionIdentifier.MUSIC:
        return draft?.links.sort(getSortMusicFn(draft)) ?? [];
      case SectionIdentifier.MERCH:
        return merch;
      case SectionIdentifier.EVENTS:
        return events;
    }
  }, [draft, sectionId, merch, events]);

  if (!draft) return null;

  const handleReorder = (direction: "up" | "down") => {
    const { itemsConfig = EMPTY_ITEMS_CONFIG } = draft;
    let currentSectionConfig = itemsConfig[sectionId];

    relevantItems.forEach((item, idx) => {
      if (!item.id) return;
      if (!currentSectionConfig[item.id]) {
        currentSectionConfig = insertItem(currentSectionConfig, item.id, {
          isHidden: false,
          order: idx + 1,
        });
      }
    });

    const orderToUpdate = forMainPageOrder
      ? currentSectionConfig[itemId].mainPageOrder
      : currentSectionConfig[itemId].order;
    if (orderToUpdate === undefined) return;

    const newOrder = orderToUpdate + (direction === "up" ? -1 : 1);

    let updatedSectionConfig = (
      forMainPageOrder ? updateMainOrder : updateOrder
    )(currentSectionConfig, itemId, newOrder);

    const redundantConfigKeys = Object.keys(updatedSectionConfig).filter(
      (id) => {
        const relevantItemIds = relevantItems
          .map((item) => item.id ?? "")
          .filter(Boolean);
        return !relevantItemIds.includes(id);
      }
    );

    redundantConfigKeys.forEach((id) => {
      updatedSectionConfig = deleteItem(updatedSectionConfig, id);
    });

    const updatedItemsConfig: ItemsConfig = {
      ...itemsConfig,
      [sectionId]: updatedSectionConfig,
    };

    onItemsReorder(updatedItemsConfig);
  };

  return (
    <OrderButtons onReorder={handleReorder} disabledState={disabledState} />
  );
};
